import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { apiService } from '../services/api';
import { useAuth } from '../services/AuthContext';
import { ZeugnisHistoryEntry } from '../types';
import { apiErrorMessage } from '../utils/apiError';

interface ZeugnisHistoryDialogProps {
  chargeId: number;
  chargeNumber: string;
  printingBlocked?: boolean;
  open: boolean;
  onClose: () => void;
}

function formatWhen(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('de-CH', {
    timeZone: 'Europe/Zurich',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function vorlageLabel(value: string): string {
  if (value === 'kunde') return 'Kundenzertifikat';
  if (value === 'zoll_eu') return 'Zollmaterialzertifikat EU';
  return value;
}

function materialLabel(value: string): string {
  if (value === 'EDELSTAHL') return 'Edelstahl';
  if (value === 'ALU') return 'Aluminium';
  return value;
}

function filled(value: string | null | undefined): string | null {
  const text = (value ?? '').trim();
  return text.length > 0 ? text : null;
}

function detailLine(row: ZeugnisHistoryEntry): string {
  const parts = [
    materialLabel(row.material_type),
    filled(row.grade_name),
    filled(row.article_number),
    filled(row.supplier_certificate_number)
      ? `Lieferantenzertifikat ${filled(row.supplier_certificate_number)}`
      : null,
    filled(row.heat_number) ? `Schmelznummer ${filled(row.heat_number)}` : null,
  ].filter((part): part is string => part !== null);
  return parts.join(' · ');
}

const ZeugnisHistoryDialog: React.FC<ZeugnisHistoryDialogProps> = ({
  chargeId,
  chargeNumber,
  printingBlocked = false,
  open,
  onClose,
}) => {
  const [rows, setRows] = useState<ZeugnisHistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { isKalotteUser } = useAuth();
  const [downloading, setDownloading] = useState<string | null>(null);

  const download = async (row: ZeugnisHistoryEntry, format: 'xlsx' | 'pdf') => {
    const key = `${row.id}-${format}`;
    setDownloading(key);
    setError(null);
    try {
      const { blob, filename } = await apiService.downloadKalotteCert(
        chargeId,
        format,
        undefined,
        'kunde',
        false,
        row.id
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const fallback = 'Das Zeugnis konnte nicht geladen werden.';
      const responseData = (err as { response?: { data?: unknown } })?.response?.data;
      if (responseData instanceof Blob) {
        try {
          const parsed = JSON.parse(await responseData.text());
          setError(apiErrorMessage({ response: { data: parsed } }, fallback));
        } catch {
          setError(fallback);
        }
      } else {
        setError(apiErrorMessage(err, fallback));
      }
    } finally {
      setDownloading(null);
    }
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setRows(null);
    setError(null);
    apiService
      .listZeugnisHistory(chargeId)
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(apiErrorMessage(err, 'Die Zeugnis-Historie konnte nicht geladen werden.'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, chargeId]);

  const countLabel =
    rows === null
      ? null
      : rows.length === 1
        ? '1 aufgezeichnetes Zeugnis.'
        : `${rows.length} aufgezeichnete Zeugnisse.`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ wordBreak: 'break-word' }}>
        Zeugnis-Historie
        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
          Charge {chargeNumber}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          {printingBlocked && (
            <Alert severity="warning">
              Das Lieferanten Zertifikat ist noch unvollständig. Ein Zeugnis darf erst nach dem
              vollständigen Speichern gedruckt werden.
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            Ein erneutes Erstellen derselben Fassung erscheint hier nicht noch einmal. Ändern sich
            die bescheinigten Werte, entsteht beim nächsten Erstellen eine neue Zeile.
            {printingBlocked
              ? ' Der Download bleibt gesperrt, solange das Lieferanten Zertifikat unvollständig ist.'
              : ` Jedes aufgezeichnete Zeugnis kann hier erneut als ${
                  isKalotteUser ? 'Excel oder PDF' : 'PDF'
                } geladen werden.`}
          </Typography>
          {rows === null && error === null && <CircularProgress size={22} />}
          {error && <Alert severity="error">{error}</Alert>}
          {rows && rows.length === 0 && (
            <Typography>Für diese Charge wurde noch kein Zeugnis aufgezeichnet.</Typography>
          )}
          {countLabel && rows && rows.length > 0 && (
            <Typography variant="body2">{countLabel}</Typography>
          )}
          {rows &&
            rows.map((row) => (
              <Box
                key={row.id}
                sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {row.zeugnis_number}
                  </Typography>
                  <Typography variant="body2">
                    {row.superseded_at
                      ? `Ersetzt am ${formatWhen(row.superseded_at)}`
                      : 'Aktuell'}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {formatWhen(row.issued_at)} · {vorlageLabel(row.vorlage)}
                  {filled(row.issued_by_name) ? ` · ${filled(row.issued_by_name)}` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {detailLine(row)}
                </Typography>
                {!printingBlocked && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {isKalotteUser && (
                    <Button
                      size="small"
                      variant="outlined"
                      aria-label={`Excel für Zeugnis ${row.zeugnis_number}`}
                      disabled={downloading !== null}
                      onClick={() => void download(row, 'xlsx')}
                    >
                      {downloading === `${row.id}-xlsx` ? 'Lädt…' : 'Excel'}
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    aria-label={`PDF für Zeugnis ${row.zeugnis_number}`}
                    disabled={downloading !== null}
                    onClick={() => void download(row, 'pdf')}
                  >
                    {downloading === `${row.id}-pdf` ? 'Lädt…' : 'PDF'}
                  </Button>
                </Box>
                )}
              </Box>
            ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ZeugnisHistoryDialog;
